"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Lock, Mail, ShieldCheck, User } from "lucide-react";
import { useTranslations } from "next-intl";

function formatCountdown(s: number) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

type Step = "form" | "otp";

export function RegisterForm() {
  const t = useTranslations("Auth");
  const router = useRouter();

  const [step, setStep] = useState<Step>("form");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPass, setShowPass] = useState(false);

  // form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nameFocused, setNameFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passFocused, setPassFocused] = useState(false);

  // otp
  const [digits, setDigits] = useState<string[]>(Array(5).fill(""));
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const otp = digits.join("");

  function handleDigitChange(i: number, value: string) {
    const char = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = char;
    setDigits(next);
    if (char && i < 4) digitRefs.current[i + 1]?.focus();
  }

  function handleDigitKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      const next = [...digits];
      next[i - 1] = "";
      setDigits(next);
      digitRefs.current[i - 1]?.focus();
    }
  }

  function handleDigitPaste(e: React.ClipboardEvent) {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 5);
    if (!text) return;
    e.preventDefault();
    const next = Array(5).fill("");
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    digitRefs.current[Math.min(text.length, 4)]?.focus();
  }

  async function handleRegister(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(data?.error ?? "Pendaftaran gagal. Coba lagi.");
        setLoading(false);
        return;
      }
      setDigits(Array(5).fill(""));
      setCountdown(120);
      setStep("otp");
    } catch {
      setError("Tidak dapat terhubung. Periksa koneksi Anda.");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/verify-email", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(data?.error ?? "Verifikasi gagal.");
        setLoading(false);
        return;
      }
      router.push("/login?verified=1");
    } catch {
      setError("Tidak dapat terhubung. Periksa koneksi Anda.");
      setLoading(false);
    }
  }

  async function handleResend() {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/resend-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(data?.error ?? "Gagal mengirim ulang OTP.");
        return;
      }
      setDigits(Array(5).fill(""));
      setCountdown(120);
      digitRefs.current[0]?.focus();
    } catch {
      setError("Tidak dapat terhubung.");
    } finally {
      setLoading(false);
    }
  }

  const strength = password.length === 0 ? 0 : password.length < 8 ? 1 : password.length < 12 ? 2 : 3;
  const strengthColor = ["", "bg-red-400", "bg-yellow-400", "bg-green-400"][strength];
  const strengthLabel = ["", "Lemah", "Sedang", "Kuat"][strength];

  if (step === "otp") {
    return (
      <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
        {/* Info */}
        <div className="rounded-xl border border-white/8 bg-white/[0.04] px-4 py-3 text-center">
          <p className="text-xs text-white/50">Kode 5 digit dikirim ke</p>
          <p className="mt-0.5 text-sm font-semibold text-white">{email}</p>
        </div>

        {error && (
          <div role="alert" className="flex items-start gap-2.5 rounded-xl bg-red-500/15 px-4 py-3 text-sm text-red-300">
            <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red-400" />
            {error}
          </div>
        )}

        {/* 5 digit boxes */}
        <div className="flex gap-2" onPaste={handleDigitPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { digitRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleDigitKeyDown(i, e)}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={i === 0}
              className="h-14 w-full rounded-xl border border-white/15 bg-white/[0.06] text-center text-2xl font-bold text-white caret-primary outline-none transition-colors focus:border-primary focus:bg-white/10"
            />
          ))}
        </div>

        {/* Countdown */}
        <div className="text-center">
          {countdown > 0 ? (
            <p className="text-xs text-white/40">
              Kirim ulang dalam{" "}
              <strong className="tabular-nums text-white/70">{formatCountdown(countdown)}</strong>
            </p>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={loading}
              className="text-xs font-semibold text-primary disabled:opacity-50"
            >
              Kirim ulang kode
            </button>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || otp.length !== 5}
          className="w-full rounded-xl py-3.5 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-50"
          style={
            otp.length === 5 && !loading
              ? { background: "linear-gradient(to right, #f16606, #c0392b)", boxShadow: "0 8px 24px rgba(241,102,6,0.35)" }
              : { background: "rgba(255,255,255,0.08)" }
          }
        >
          {loading ? "Memverifikasi..." : "Verifikasi Email"}
        </button>

        <button
          type="button"
          onClick={() => { setStep("form"); setError(null); setCountdown(0); }}
          className="text-center text-xs text-white/40 underline"
        >
          Kembali & ubah email
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleRegister} className="flex flex-col gap-4">
      {error && (
        <div role="alert" className="flex items-start gap-2.5 rounded-xl bg-red-500/15 px-4 py-3 text-sm text-red-300">
          <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red-400" />
          {error}
        </div>
      )}

      {/* Nama */}
      <div
        className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-all ${
          nameFocused ? "border-primary/60 bg-white/5 shadow-[0_0_0_3px_rgba(241,102,6,0.15)]" : "border-white/10 bg-white/[0.04]"
        }`}
      >
        <User className="size-4 shrink-0 text-white/40" aria-hidden />
        <input
          type="text"
          name="name"
          placeholder="Nama lengkap"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={() => setNameFocused(true)}
          onBlur={() => setNameFocused(false)}
          className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30"
        />
      </div>

      {/* Email */}
      <div
        className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-all ${
          emailFocused ? "border-primary/60 bg-white/5 shadow-[0_0_0_3px_rgba(241,102,6,0.15)]" : "border-white/10 bg-white/[0.04]"
        }`}
      >
        <Mail className="size-4 shrink-0 text-white/40" aria-hidden />
        <input
          type="email"
          name="email"
          placeholder={t("email")}
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={() => setEmailFocused(true)}
          onBlur={() => setEmailFocused(false)}
          className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30"
        />
      </div>

      {/* Password */}
      <div className="flex flex-col gap-1.5">
        <div
          className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-all ${
            passFocused ? "border-primary/60 bg-white/5 shadow-[0_0_0_3px_rgba(241,102,6,0.15)]" : "border-white/10 bg-white/[0.04]"
          }`}
        >
          <Lock className="size-4 shrink-0 text-white/40" aria-hidden />
          <input
            type={showPass ? "text" : "password"}
            name="password"
            placeholder={t("password")}
            autoComplete="new-password"
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setPassFocused(true)}
            onBlur={() => setPassFocused(false)}
            className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30"
          />
          <button
            type="button"
            onClick={() => setShowPass((v) => !v)}
            className="shrink-0 text-white/30 hover:text-white/60"
            aria-label={showPass ? "Sembunyikan" : "Tampilkan"}
          >
            {showPass ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
          </button>
        </div>
        {password.length > 0 && (
          <div className="flex items-center gap-2 px-1">
            <div className="flex flex-1 gap-1">
              {[1, 2, 3].map((n) => (
                <div key={n} className={`h-1 flex-1 rounded-full transition-all ${strength >= n ? strengthColor : "bg-white/10"}`} />
              ))}
            </div>
            <span className={`text-[10px] font-bold ${["", "text-red-400", "text-yellow-400", "text-green-400"][strength]}`}>
              {strengthLabel}
            </span>
          </div>
        )}
      </div>

      {/* Consent */}
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-3 py-3">
        <div className="relative mt-0.5 shrink-0">
          <input type="checkbox" required className="size-4 appearance-none rounded border border-white/25 bg-white/10 checked:border-primary checked:bg-primary" />
          <ShieldCheck className="pointer-events-none absolute inset-0 hidden size-4 text-white peer-checked:block" aria-hidden />
        </div>
        <span className="text-xs leading-relaxed text-white/50">
          {t("consentPrefix")}{" "}
          <a href="#" className="text-primary/80 underline hover:text-primary">{t("terms")}</a>{" "}
          {t("and")}{" "}
          <a href="#" className="text-primary/80 underline hover:text-primary">{t("privacyPolicy")}</a>{" "}
          {t("consentSuffix")}
        </span>
      </label>

      <button
        type="submit"
        disabled={loading}
        className="mt-1 w-full rounded-xl py-3.5 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-60"
        style={{ background: "linear-gradient(to right, #f16606, #c0392b)", boxShadow: "0 8px 24px rgba(241,102,6,0.35)" }}
      >
        {loading ? "Mendaftarkan..." : t("registerSubmit")}
      </button>
    </form>
  );
}
